import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

export function useDebouncedState<T>(initial: T, delay = 300, resetKey?: unknown) {
  const [state, setState] = useState({ actual: initial, debounced: initial, resetKey });
  let currentState = state;
  if (!Object.is(state.resetKey, resetKey)) {
    currentState = { actual: initial, debounced: state.debounced, resetKey };
    setState(currentState);
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setState((current) =>
        Object.is(current.actual, current.debounced) ? current : { ...current, debounced: current.actual },
      );
    }, delay);
    return () => clearTimeout(timeout);
  }, [currentState.actual, delay]);

  const setActual: Dispatch<SetStateAction<T>> = (nextValue) => {
    setState((current) => ({
      ...current,
      actual: typeof nextValue === 'function' ? (nextValue as (currentValue: T) => T)(current.actual) : nextValue,
    }));
  };

  return [{ actual: currentState.actual, debounced: currentState.debounced }, setActual] as const;
}
